import React from 'react';
import { cn } from '@express-labs/raven-tools';
import ProductImage from '../shared/ProductImage/ProductImage';
import ProductTitle from '../shared/ProductTitle/ProductTitle';
import ProductTag from '../shared/ProductTag/ProductTag';
import ProductAvailability from '../shared/ProductAvailability/ProductAvailability';
import ProductColors from '../shared/ProductColors/ProductColors';
import IconV2 from '../../../IconV2/IconV2';
import Price from '../../../Price/Price';
import StarRating from '../../../StarRating/StarRating';
import s from './VerticalProductCard.scss';
import { VerticalProductCardProps } from '../../productCardTypes';

const VerticalProductCard: React.FC<VerticalProductCardProps> = ({
  canUseRouter = true,
  additionalColorsLink,
  children,
  className,
  colors = {},
  descriptionWrapperClassName = '',
  enableExpressView = false,
  EFOProduct = false,
  onExpressViewClick,
  imageHeight,
  imageWidth,
  isAvailableInStore = false,
  isEnsemble,
  isNew = false,
  isScrollable = false,
  listPrice,
  marketplaceProduct = false,
  name,
  onCardClick,
  onImageError,
  onImageLoad,
  onlineExclusive = false,
  productId,
  productImage,
  productImageSet = [],
  productLinkTitle,
  productURL,
  promoMessage,
  rating = 0,
  salePrice,
  showCarouselButtons,
  storeAvailabilityMessage,
  totalReviewCount = 0,
  hideColorSwatch = false,
}) => {
  return (
    <div className={cn(s.productVContainer, className)}>
      <div
        className={cn(
          s.imageContainer,
          isScrollable && s.scrollableImageContainer
        )}
      >
        {isScrollable && (
          <ProductImage
            alt={name}
            canUseRouter={canUseRouter} // we need this to ensure that React Router is never used in raven-header
            height={imageHeight}
            isScrollable
            onError={onImageError}
            onImageClick={onCardClick}
            onLoad={onImageLoad}
            productId={productId}
            productImageSet={productImageSet}
            productURL={productURL}
            shouldShowCarouselButtons={showCarouselButtons}
            width={imageWidth}
          />
        )}
        {!isScrollable && (
          <ProductImage
            alt={name}
            canUseRouter={canUseRouter} // we need this to ensure that React Router is never used in raven-header
            height={imageHeight}
            isScrollable={false}
            onError={onImageError}
            onImageClick={onCardClick}
            onLoad={onImageLoad}
            productId={productId}
            productImage={productImage}
            productURL={productURL}
            width={imageWidth}
          />
        )}
        {enableExpressView && (
          <button
            className={cn(
              s.expressViewBtn,
              isScrollable && s.scrollableImageExpressViewBtn
            )}
            data-productid={productId}
            data-testid="expressView-button"
            onClick={onExpressViewClick}
            role="button"
            tabIndex={0}
          >
            <IconV2
              className={s.quickViewIcon}
              icon="shopping-bag"
              size="small"
            />
          </button>
        )}
      </div>
      <div className={cn(s.productVDescription, descriptionWrapperClassName)}>
        <ProductColors
          additionalColorsLink={additionalColorsLink || productURL}
          className={s.productColors}
          colorSwatches={colors.colorSwatches}
          onChangeColor={colors.onChangeColor}
          selectedSkuUpc={colors.selectedSkuUpc}
          productId={productId}
          hideColorSwatch={hideColorSwatch}
        />
        <ProductTag
          EFOProduct={EFOProduct}
          isNew={isNew}
          marketplaceProduct={marketplaceProduct}
          onlineExclusive={onlineExclusive}
        />
        <ProductTitle
          containerClassName={s.productTitle}
          name={name}
          productId={productId}
          productLinkTitle={productLinkTitle}
          productURL={productURL}
        />
        {listPrice && (
          <div className={s.productPrice}>
            {isEnsemble ? (
              <Price
                promoMessageContainerClassName={s.promoMessageContainer}
                isEnsemble={isEnsemble}
                layout="stacked"
                listPrice={listPrice}
                productId={productId}
                promoMessage={promoMessage}
                salePrice={salePrice}
              />
            ) : (
              <Price
                layout="stacked"
                listPrice={listPrice}
                promoMessage={promoMessage}
                salePrice={salePrice}
              />
            )}
          </div>
        )}
        {rating > 0 && (
          <StarRating
            rating={rating}
            size="large"
            totalReviewCount={totalReviewCount}
            productReviewsURL={`${productURL}#reviews`}
          />
        )}
        <ProductAvailability
          isAvailableInStore={isAvailableInStore}
          storeAvailabilityMessage={storeAvailabilityMessage}
        />
        {children}
      </div>
    </div>
  );
};

export default VerticalProductCard;
